import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { getNewsArticleBySlug } from "../../firebase";
import { NewsArticle } from "../../types";
import Header from "../../components/layout/Header";
import SEOHead from "../../components/SEOHead";

const NewsDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNewsArticle = async () => {
      if (!slug) {
        setError("Article slug is missing");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const fetchedArticle = await getNewsArticleBySlug(slug);

        // If article doesn't exist or is not published, redirect to news list
        if (!fetchedArticle || !fetchedArticle.published) {
          navigate("/news");
          return;
        }

        setArticle(fetchedArticle);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching news article:", err);
        setError("Failed to load news article. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchNewsArticle();
  }, [slug, navigate]);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const createMarkup = (htmlContent: string) => {
    return { __html: htmlContent };
  };

  // Get a preview of the article content for meta description
  const getContentPreview = (content: string) => {
    const plainText = content.replace(/<[^>]+>/g, "");
    return plainText.length > 160 ? plainText.substring(0, 160) + "..." : plainText;
  };

  return (
    <div className="py-52 bg-white min-h-screen">
      <Header />

      {/* SEO Meta Tags */}
      {article && (
        <SEOHead
          title={`${article.title} - Wattlenest`}
          description={getContentPreview(article.body)}
          image={article.imageURL || '/wattlenest.png'}
          url={`/news/${article.slug}`}
          type="article"
          publishedTime={new Date(article.createdAt).toISOString()}
          modifiedTime={new Date(article.updatedAt).toISOString()}
          author="Wattlenest"
        />
      )}

      <div className="pb-16">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          {/* Back button */}
          <Link
            to="/news"
            className="inline-flex items-center mb-8 text-sm font-medium text-gold hover:text-gold-600"
          >
            <svg
              className="mr-2 h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to News
          </Link>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
            </div>
          ) : error ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <p className="text-red-500 mb-4">{error}</p>
              <Link
                to="/news"
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gold hover:bg-gold-600"
              >
                Return to News
              </Link>
            </div>
          ) : article ? (
            <article className="bg-white rounded-lg shadow-lg overflow-hidden">
              {article.imageURL && (
                <div className="w-full">
                  <img
                    src={article.imageURL}
                    alt={article.title}
                    className="w-full h-64 sm:h-96 object-cover"
                  />
                </div>
              )}

              <div className="p-6 sm:p-8">
                <p className="text-sm font-medium text-gold mb-2">
                  {formatDate(article.createdAt)}
                </p>

                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                  {article.title}
                </h1>

                <div className="border-t border-gray-200 my-6"></div>

                <div
                  className="prose prose-lg max-w-none text-gray-700 prose-headings:text-gray-900 prose-headings:font-semibold prose-h2:text-2xl prose-h3:text-xl prose-a:text-gold prose-a:no-underline hover:prose-a:underline prose-img:rounded-lg"
                  dangerouslySetInnerHTML={createMarkup(article.body)}
                />
              </div>
            </article>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <p className="text-gray-700 mb-4">Article not found</p>
              <Link
                to="/news"
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gold hover:bg-gold-600"
              >
                Return to News
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsDetail;
